/**
 * 종목 상세 Drawer — 주식/ETF 클릭 시 표시
 * 보유 계좌 요약 + 캔들차트/종목정보/뉴스는 공통 컴포넌트 위임
 * 국내 종목은 .KS/.KQ 접미사 제거 후 KIS API 호출 (실증: 005930만 정상)
 */
import { Card, Drawer, Progress, Statistic, Table } from "antd";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import StockCandleChart from "src/components/StockCandleChart";
import StockInfoCard from "src/components/StockInfoCard";
import StockNewsCard from "src/components/StockNewsCard";
import StockHistoryCard from "src/components/StockHistoryCard";
import DcfValuationCard from "src/components/DcfValuationCard";
import { AccountTag } from "src/lib/brokerage";
import { currency, formatWon } from "src/lib/format";
import { useScreenStore } from "src/stores/screenStore";
import { useOverlayBackKey } from "src/hooks/useOverlayBackKey";
import { marketApi } from "src/api/market";
import { stocksApi } from "src/api/stocks";

/** positions의 stock_id(.KS) → KIS 심볼(순수 코드) 변환. 해외는 그대로 */
export const toKisSymbol = (stockId: string): string =>
  /\.(KS|KQ)$/.test(stockId) ? stockId.slice(0, -3) : stockId;

/** 해외 주식 판별 — 접미사 없으면 숫자 6자리(국내 코드)인지로 판별 (KIS API는 접미사 불필요) */
export const isOverseasStock = (stockId: string): boolean =>
  !/\.(KS|KQ)$/.test(stockId) && !/^\d{6}$/.test(stockId);

/** RSI(14) 마지막 값 — 레거시 calculateRsiList와 동일한 Wilder 평활식 (admin 오늘 주가 변동 호환) */
const calcRsi14 = (closes: number[], period = 14): number | null => {
  if (closes.length < period + 1) return null;
  let gain = 0,
    loss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) gain += diff;
    else loss += Math.abs(diff);
  }
  gain /= period;
  loss /= period;
  let rsi = 100 - 100 / (1 + gain / (loss || Number.EPSILON));
  for (let i = period + 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff > 0) {
      gain = (gain * (period - 1) + diff) / period;
      loss = (loss * (period - 1)) / period;
    } else {
      gain = (gain * (period - 1)) / period;
      loss = (loss * (period - 1) + Math.abs(diff)) / period;
    }
    rsi = 100 - 100 / (1 + gain / (loss || Number.EPSILON));
  }
  return rsi;
};

interface HoldingRow {
  key: string;
  brokerage: string;
  account: string;
  quantity: number;
  avgPrice: number;
  currentValue: number;
  gain: number;
  gainRate: number;
}

interface StockDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  stockId: string;
  stockName: string;
  /** 표시할 사용자 범위 — 화면의 사용자 필터 전달 (미전달 시 전체 사용자) */
  userIds?: string[];
  /** 보유 정보 (positions 행 배열 — 페이지에서 전달) */
  holdings?: {
    user_id: string;
    brokerage: string;
    account_type: string;
    current_quantity: number;
    avg_price: number;
    total_value_krw: number;
    unrealized_gain_krw: number;
    unrealized_gain_percent: number;
  }[];
}

const StockDetailDrawer = ({ open, onClose, stockId, stockName, userIds, holdings = [] }: StockDetailDrawerProps) => {
  const screens = useScreenStore((s) => s.screens);
  // 백키로도 드로어가 닫히도록 (뒤로가기 → 이전 화면 이동 방지)
  useOverlayBackKey(open, onClose);
  const symbol = toKisSymbol(stockId);
  const overseas = isOverseasStock(stockId);
  // 모바일=전체 폭, 태블릿=88%, 데스크탑=720px 넓게
  const drawerWidth = !screens.md ? "100%" : !screens.xl ? "88vw" : 720;

  const holdingRows: HoldingRow[] = useMemo(
    () =>
      holdings.map((h, i) => ({
        key: `${h.user_id}-${h.brokerage}-${h.account_type}-${i}`,
        brokerage: h.brokerage,
        account: `${h.brokerage} ${h.account_type}`,
        quantity: h.current_quantity,
        avgPrice: h.avg_price,
        currentValue: h.total_value_krw,
        gain: h.unrealized_gain_krw,
        gainRate: h.unrealized_gain_percent * 100, // 소수 단위 → percent
      })),
    [holdings],
  );

  const totalValue = holdingRows.reduce((s, r) => s + r.currentValue, 0);
  const totalGain = holdingRows.reduce((s, r) => s + r.gain, 0);

  // 실시간 현재가·당일 등락 (KIS price API)
  const priceQ = useQuery({
    queryKey: ["market", "price", symbol, overseas],
    queryFn: () => marketApi.getPrice(symbol, overseas),
    enabled: open,
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
  });
  const px = priceQ.data;
  const priceFmt = px
    ? overseas
      ? `$${px.current_price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `${px.current_price.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}원`
    : null;
  const chgFmt = px
    ? `${px.change_amount > 0 ? "+" : ""}${overseas ? `$${Math.abs(px.change_amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${px.change_amount.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}원`} (${px.change_rate > 0 ? "+" : ""}${px.change_rate.toFixed(2)}%)`
    : null;

  // 오늘 주가 변동 — 캔들 시리즈 마지막 2봉 OHLCV + RSI14 (캔들차트 기본 6M과 같은 쿼리키로 캐시 공유)
  const chartQ = useQuery({
    queryKey: ["market", "chart", symbol, 130, overseas],
    queryFn: () => marketApi.getChart(symbol, 130, overseas),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const candles = useMemo(() => chartQ.data?.candles ?? [], [chartQ.data]);
  const todayC = candles.length > 0 ? candles[candles.length - 1] : null;
  const prevC = candles.length > 1 ? candles[candles.length - 2] : null;
  const todayRsi = useMemo(
    () => calcRsi14(candles.map((c) => c.close)),
    [chartQ.data],
  );
  const fmtPx = (v: number | null | undefined) =>
    v == null
      ? "-"
      : overseas
        ? `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : `${v.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}원`;
  const dayDiff = todayC && prevC ? todayC.close - prevC.close : null;
  const dayDiffPct = todayC && prevC && prevC.close ? (dayDiff! / prevC.close) * 100 : null;
  const signedPx = (v: number | null) =>
    v == null
      ? "-"
      : `${v > 0 ? "+" : v < 0 ? "-" : ""}${overseas ? `$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${Math.abs(v).toLocaleString("ko-KR", { maximumFractionDigits: 0 })}원`}`;

  // 회복률 (52주 고점 대비) — 단일 종목 조회
  const recoveryQ = useQuery({
    queryKey: ["stocks", "recovery", stockId],
    queryFn: () => stocksApi.getRecoveryRate(stockId),
    enabled: open,
    staleTime: 10 * 60_000,
  });
  const rec = useMemo(() => {
    const data = recoveryQ.data as any;
    const stocks = data?.stocks || data?.data?.stocks || [];
    return stocks[0] || null;
  }, [recoveryQ.data]);

  // 회복률 구간 판정 + 상황 설명·일반적 대응 조언
  const recoveryAdvice = useMemo(() => {
    if (!rec) return null;
    const rr = rec.recovery_rate ?? 0;
    if (rr >= 90)
      return {
        color: "#e67e22", label: "고점권",
        situation: `52주 고점(${rec.week52_high?.toLocaleString("ko-KR", { maximumFractionDigits: 0 })})에서 10% 이내로 올라온 상태입니다. 추가 상승 여력은 ${rec.upside_to_high?.toFixed(1)}% 남았습니다.`,
        advice: "과열 구간 — 추격 매수보다 보유 유지가 원칙. 수익 부분 실현을 고려할 시점이며, 신규 매수는 분할보다 관망이 유리합니다.",
      };
    if (rr >= 70)
      return {
        color: "#27ae60", label: "상승 중",
        situation: `고점 대비 ${rr.toFixed(0)}% 회복된 상태로 상승 추세입니다. 상승 여력은 ${rec.upside_to_high?.toFixed(1)}% 남았습니다.`,
        advice: "추세가 이어지면 보유 유지. 추가 매수는 눌림목(단기 조정)을 기다렸다가 분할 접근하는 편이 안전합니다.",
      };
    if (rr >= 40)
      return {
        color: "#1677ff", label: "중간권",
        situation: `고점 대비 ${rr.toFixed(0)}% 회복, 여전히 고점에서 ${rec.drawdown?.toFixed(1)}% 아래 있습니다.`,
        advice: "방향이 확인되는 구간 — 일시 분할 매수 후 가격이 지지되면 비중을 늘리는 방식이 일반적입니다. 하락 전환 시 다음 지지선까지의 여유를 확인하세요.",
      };
    if (rr >= 15)
      return {
        color: "#faad14", label: "저점 부근",
        situation: `고점에서 ${rec.drawdown?.toFixed(1)}% 급락한 수준입니다. 52주 저점(${rec.week52_low?.toLocaleString("ko-KR", { maximumFractionDigits: 0 })})까지의 여유는 ${rr >= 5 ? "제한적" : "얼마 안 됩니다"} — 회복 여력은 +${rec.upside_to_high?.toFixed(1)}%입니다.`,
        advice: "캡틴의 가치투자 원칙(할인존 분할 매수)에 부합하는 구간. 기업 가치에 문제가 없다면 분할 매수를 시작할 위치이며, 고점 회복 시 큰 수익을 기대할 수 있습니다. 단, 급락 원인(실적 악화 등)을 먼저 확인하세요.",
      };
    return {
      color: "#cf1322", label: "바닥권",
      situation: `52주 고점에서 ${rec.drawdown?.toFixed(1)}%나 하락한 바닥권입니다. 회복 여력은 +${rec.upside_to_high?.toFixed(1)}%로 크지만, 이만큼 떨어진 데엔 이유가 있을 수 있습니다.`,
      advice: "좌표적 매수 기회이지만 원인 확인 필수 — 실적·섹터 이슈가 일시인지 구조적인지 판단 후 소액부터 분할 진입. 저점 매수는 시간이 걸린다는 전제로 여유 자금으로만 접근하세요.",
    };
  }, [rec]);

  return (
    <Drawer
      title={
        <span>
          {stockName} <span style={{ fontSize: 12, color: "#999", fontWeight: 400 }}>{stockId}</span>
        </span>
      }
      open={open}
      onClose={onClose}
      width={drawerWidth}
      styles={{ body: { padding: "12px 16px" } }}
    >
      {/* 실시간 현재가·당일 등락 */}
      {priceFmt && chgFmt && px && (
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 700 }}>{priceFmt}</span>
          <span className={px.change_amount > 0 ? "fred" : px.change_amount < 0 ? "fblue" : ""} style={{ fontWeight: 600, fontSize: 14 }}>
            {px.change_amount > 0 ? "▲" : px.change_amount < 0 ? "▼" : ""} {chgFmt}
          </span>
        </div>
      )}

      {/* 오늘 주가 변동 (admin StockPriceToday 동일 구성: OHLCV + RSI + 전일대비) */}
      {todayC && (
        <Card
          size="small"
          title="오늘 주가 변동"
          style={{ marginBottom: 12 }}
          styles={{ body: { padding: "8px 14px" } }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: screens.sm ? "repeat(4, 1fr)" : "repeat(2, 1fr)",
              gap: "6px 10px",
              fontSize: 13,
            }}
          >
            {[
              ["저가", fmtPx(todayC.low), ""],
              ["고가", fmtPx(todayC.high), ""],
              ["시가", fmtPx(todayC.open), ""],
              ["종가", fmtPx(todayC.close), ""],
              ["거래량", (todayC.volume ?? 0).toLocaleString(), ""],
              [
                "RSI",
                todayRsi != null ? todayRsi.toFixed(2) : "-",
                // 과매수 ≥70 적색 / 과매도 ≤30 청색 (매매 시점 페이지 rsiColor 컨벤션 동일)
                todayRsi == null ? "" : todayRsi >= 70 ? "fred" : todayRsi <= 30 ? "fblue" : "",
              ],
            ].map(([label, value, cls]) => (
              <div key={label}>
                <div style={{ fontSize: 11, opacity: 0.55 }}>{label}</div>
                <div className={cls} style={{ fontWeight: 600 }}>{value}</div>
              </div>
            ))}
            <div>
              <div style={{ fontSize: 11, opacity: 0.55 }}>전일대비</div>
              <div className={(dayDiff ?? 0) > 0 ? "fred" : (dayDiff ?? 0) < 0 ? "fblue" : ""} style={{ fontWeight: 600 }}>
                {signedPx(dayDiff)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.55 }}>전일대비(%)</div>
              <div className={(dayDiffPct ?? 0) > 0 ? "fred" : (dayDiffPct ?? 0) < 0 ? "fblue" : ""} style={{ fontWeight: 600 }}>
                {dayDiffPct == null ? "-" : `${dayDiffPct > 0 ? "+" : ""}${dayDiffPct.toFixed(2)}%`}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* 보유 요약 (전달받은 경우) */}
      {holdingRows.length > 0 && (
        <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "10px 14px" } }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 4 }}>
            <Statistic
              title={`보유 평가 (${holdingRows.length}계좌)`}
              value={formatWon(totalValue)}
              formatter={(v) => <span style={{ fontSize: 17, fontWeight: 700 }}>{v as string}</span>}
            />
            <span className={totalGain >= 0 ? "fred" : "fblue"} style={{ fontWeight: 600 }}>
              {totalGain >= 0 ? "+" : ""}{formatWon(totalGain)}
            </span>
          </div>
          <Table
            size="small"
            dataSource={holdingRows}
            rowKey="key"
            pagination={false}
            style={{ marginTop: 8 }}
            columns={[
              { title: "계좌", dataIndex: "account", render: (v: string) => <AccountTag account={v} /> },
              { title: "수량", dataIndex: "quantity", align: "right" as const, render: (v: number) => v.toLocaleString() },
              { title: "평단가", dataIndex: "avgPrice", align: "right" as const, render: (v: number) => v.toLocaleString("ko-KR", { maximumFractionDigits: 0 }) },
              { title: "평가금액", dataIndex: "currentValue", align: "right" as const, render: (v: number) => currency(v) },
              {
                title: "손익", dataIndex: "gain", align: "right" as const,
                render: (v: number, r: HoldingRow) => (
                  <span className={v > 0 ? "fred" : v < 0 ? "fblue" : ""}>
                    {v > 0 ? "+" : ""}{formatWon(v)} <span style={{ fontSize: 11 }}>({r.gainRate > 0 ? "+" : ""}{r.gainRate.toFixed(2)}%)</span>
                  </span>
                ),
              },
            ] as any}
          />
        </Card>
      )}

      {/* 회복률 — 52주 고점 대비 위치 + 구간별 투자 조언 */}
      {recoveryAdvice && rec && (
        <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "10px 14px" } }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 4 }}>
            <span style={{ fontSize: 12, color: "#999" }}>52주 고점 대비 회복률</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: recoveryAdvice.color }}>{recoveryAdvice.label}</span>
          </div>
          <Progress
            percent={Math.round(rec.recovery_rate)}
            strokeColor={recoveryAdvice.color}
            size="small"
            style={{ margin: "4px 0 6px" }}
            format={(p) => <span style={{ fontSize: 12, fontWeight: 600 }}>{p}%</span>}
          />
          <div style={{ fontSize: 11, color: "#999", display: "flex", justifyContent: "space-between" }}>
            <span>저점 {rec.week52_low?.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}</span>
            <span>고점 {rec.week52_high?.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}</span>
          </div>
          <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "var(--app-color-bg-layout, rgba(0,0,0,0.03))" }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>{recoveryAdvice.situation}</div>
            <div style={{ fontSize: 12, fontWeight: 500 }}>
              💡 {recoveryAdvice.advice}
            </div>
          </div>
        </Card>
      )}

      {/* 가치 평가 (DCF) — 표시 전용, ETF·데이터 부재는 카드 숨김 */}
      <DcfValuationCard stockId={stockId} overseas={overseas} enabled={open} />

      {/* 캔들차트 (공통) — 수익금 뷰: 거래내역 기반 이동평균 평단가로 시점별 수익금 표시 */}
      <StockCandleChart
        symbol={symbol}
        overseas={overseas}
        height={260}
        resetKey={open}
        enabled={open}
        gainEnabled={holdingRows.length > 0}
        stockId={stockId}
        userIds={userIds}
      />

      {/* 종목 정보 (공통) */}
      <StockInfoCard symbol={symbol} overseas={overseas} columns={3} enabled={open} />

      {/* 거래·배당 내역 (공통) */}
      <StockHistoryCard stockId={stockId} enabled={open} userIds={userIds} />

      {/* 뉴스 (공통) */}
      <StockNewsCard symbol={symbol} overseas={overseas} limit={10} maxHeight={340} enabled={open} />
    </Drawer>
  );
};

export default StockDetailDrawer;
